import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import { buildChartData, donutData, disabledReasons, isDefaultSettings } from "../../../lib/chartSeries";
import { useChartPrefs } from "./useChartPrefs";
import { ChartSettingsBar } from "./ChartSettingsBar";
import { CustomChartCard } from "./CustomChartCard";

const TYPE_L = { line: "Line", area: "Area", stacked: "Stacked bar", grouped: "Grouped bar", combo: "Combo", donut: "Donut" };
const AXIS_L = { monthly: "Monthly", loan_year: "Loan year", cy: "Calendar year", fy: "Financial year", custom: "Custom range" };
const WIN_L = { past: "Past only", future: "Future only", full: "Full life" };

/** Feature 6 — chart settings bar above the Feature 5 graphs. Default settings → Feature 5 graphs unchanged. */
export default function LoanChartStudio({ loans, asOn, planName, view, setView, selectedIdx, setSelectedIdx, reportRef, children }) {
  const auth = useAuth();
  const { theme } = useTheme() || {};
  const [s, set, reset] = useChartPrefs(Boolean(auth?.user));
  const [apiData, setApiData] = useState(null);
  const mode = s.split ? "split" : view === "cumulative" ? "combined" : "individual";
  const isDefault = isDefaultSettings(s);
  const reasons = useMemo(() => disabledReasons(s), [s]);

  useEffect(() => { if (view === "individual" && s.split) set({ split: false }); }, [view, s.split, set]);

  useEffect(() => {
    if (isDefault || !loans.length) return undefined;
    let cancelled = false;
    api.post("/loans/chart-series", { loans, as_on: asOn, plan_name: planName || null, apply_rate_changes: s.applyRateChanges })
      .then(({ data }) => { if (!cancelled) setApiData(data); })
      .catch((e) => { if (!cancelled) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load chart data"); });
    return () => { cancelled = true; };
  }, [isDefault, loans, asOn, planName, s.applyRateChanges]);

  const seriesIdx = useMemo(() => {
    const i = apiData?.loans?.findIndex((l) => l.loan_name === loans[selectedIdx]?.name) ?? -1;
    return i >= 0 ? i : 0;
  }, [apiData, loans, selectedIdx]);
  const built = useMemo(() => (apiData ? buildChartData(apiData.loans, s, mode, seriesIdx) : null), [apiData, s, mode, seriesIdx]);
  const donutRows = useMemo(() => (apiData && s.chartType === "donut"
    ? donutData(apiData.loans, mode === "individual" ? "individual" : "combined", seriesIdx) : null), [apiData, s.chartType, mode, seriesIdx]);

  const onMode = (m) => {
    setView(m === "individual" ? "individual" : "cumulative");
    set({ split: m === "split" });
  };
  const who = mode === "individual" ? (loans[selectedIdx]?.name || "Loan") : mode === "split" ? "All loans (split by loan)" : "All loans (combined)";
  const title = s.chartType === "donut"
    ? `${who} — Donut · paid vs to pay as on ${apiData?.as_on || asOn}`
    : `${who} — ${TYPE_L[s.chartType]} · ${s.measure === "running" ? "Running total" : "Per period"} · ${AXIS_L[s.timeAxis]}`;
  const summary = [
    s.chartType !== "donut" && WIN_L[s.window],
    s.display === "pct" ? "% of EMI" : "Rupees",
    mode === "split" && `Stacked by loan: ${s.splitMetric}`,
    s.timeAxis === "custom" && `${s.rangeFrom || "start"} to ${s.rangeTo || "end"}`,
    s.applyRateChanges ? "Rate changes re-split" : "Rate changes as markers",
    `As on ${apiData?.as_on || asOn}`,
  ].filter(Boolean).join(" · ");

  return (
    <div className="space-y-3" data-testid="chart-studio">
      <ChartSettingsBar s={s} set={set} mode={mode} onMode={onMode} loans={loans} loanIdx={selectedIdx}
                        onLoanIdx={setSelectedIdx} reasons={reasons} isDefault={isDefault} onReset={reset} />
      {isDefault && children}
      {!isDefault && !built && <div className="text-xs text-muted-foreground">Loading chart…</div>}
      {!isDefault && built && (
        <CustomChartCard built={built} donutRows={donutRows} s={s} mode={mode} title={title}
                         summary={summary} reportRef={reportRef} theme={theme} />
      )}
    </div>
  );
}
